import { type AxiosError } from "axios";
import { useMutation, useQuery } from "@tanstack/react-query";

import type { PaginationRequest } from "@/shared/types";
import { type PaginatedResponse } from "@/shared/types";
import { useApi } from "@/shared/api";
import type {
  MasterTeamLeadAccountsResponse,
  MasterTeamLeadCredentialsResponse,
} from "./api.dto";
import { filterObjectKeys } from "@/shared/lib/utils";

export const useMasterTeamleadsTeamleads = (
  params: PaginationRequest & { masterId: string; enabled: boolean },
) => {
  const { api } = useApi();

  return useQuery<
    PaginatedResponse<MasterTeamLeadAccountsResponse>,
    AxiosError
  >({
    queryKey: [
      "masterteamleads-teamleads",
      filterObjectKeys(params, ["enabled", "masterId"]),
    ],
    queryFn: async () => {
      try {
        const response = await api.get(`/teamleads/master/${params.masterId}`, {
          params: filterObjectKeys(params, ["enabled", "masterId"]),
        });

        return response.data;
      } catch (e) {
        console.error(e);
        throw new Error(e as string | undefined);
      }
    },
    enabled: !!params.masterId && params.enabled,
  });
};

export const useUpdateTeamlead = ({ masterId }: { masterId: string }) => {
  const { api } = useApi();

  return useMutation({
    mutationFn: async (data: { token: string; name: string }) => {
      try {
        const response = await api.patch(`/teamleads/master/${masterId}`, data);

        return response.data;
      } catch (e) {
        console.error(e);
        throw new Error(e as string | undefined);
      }
    },
  });
};

export const useTransferTeamlead = ({
  fromMasterId,
  toMasterId,
}: {
  fromMasterId: string;
  toMasterId: string;
}) => {
  const { api } = useApi();

  return useMutation({
    mutationFn: async (data: { token: string; name: string }[]) => {
      try {
        const response = await api.patch(
          `/teamleads/master/${fromMasterId}/transfer/${toMasterId}`,
          data,
        );

        return response.data;
      } catch (e) {
        console.error(e);
        throw new Error(e as string | undefined);
      }
    },
  });
};

export const useTeamleadCredentials = ({
  accountId,
  enabled,
}: {
  accountId: string;
  enabled: boolean;
}) => {
  const { api } = useApi();

  return useQuery<MasterTeamLeadCredentialsResponse>({
    queryKey: ["teamlead", accountId],
    queryFn: async () => {
      try {
        const response = await api.get(`/teamleads/${accountId}/credentials`);

        return response.data;
      } catch (e) {
        console.error(e);
        throw new Error(e as string | undefined);
      }
    },
    enabled,
  });
};
